<script setup lang="ts">
import UICloseButton from '@/components/ui/UICloseButton.vue'
import UICollapseButton from '@/components/ui/UICollapseButton.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import { PhWarning } from '@phosphor-icons/vue'

withDefaults(
  defineProps<{
    /** What text should be displayed in the header? */
    text: string
    /** What variant should be used for the header? */
    variant?: keyof typeof variantMapping
    /** What should the heading font size be? */
    headingSize?: 'serif' | 'serif-sm' | 'serif-xs'
    /** What type of button should be included, if any? */
    btnVariant?: 'close' | 'collapse' | 'expand'
    /** What should the aria label be for the button (if applicable)? */
    buttonAriaLabel?: string
  }>(),
  {
    variant: 'default',
    headingSize: 'serif'
  }
)

defineEmits<{
  (e: 'btnClicked'): void
}>()

const variantMapping = {
  default: {
    container: 'bg-elevated justify-between p-6 rounded-t-[15px]',
    text: 'text-emphasis'
  },
  warn: {
    container: 'bg-warning justify-start p-6 rounded-t-[15px]',
    text: 'text-emphasis'
  },
  destructive: {
    container: 'bg-error justify-between p-6 rounded-t-[15px]',
    text: '!text-inverse'
  },
  panel: {
    container:
      'bg-default justify-between px-padding-panel-container-horz pb-stack-default sm:pb-padding-panel-container-vert sm:pt-[22px] rounded-t',
    text: 'text-default'
  },
  legend: {
    // padding provided in the parent based on expanded/collapsed state
    container: 'bg-default justify-between rounded-t',
    text: 'text-default'
  }
}
</script>

<template>
  <header
    class="text-center sm:text-left flex items-center space-x-2 w-full"
    :class="[variantMapping[variant].container]"
  >
    <PhWarning
      v-if="variant === 'warn'"
      size="24"
      weight="regular"
      class="fill-charcoal"
    />
    <UIHeading
      tag="h4"
      :variant="headingSize"
      :text="text"
      :class="variantMapping[variant].text"
    ></UIHeading>
    <UICloseButton
      v-if="btnVariant === 'close'"
      tool-tip-direction="bottom"
      :aria-label="buttonAriaLabel"
      @close="$emit('btnClicked')"
    />
    <UICollapseButton
      v-else-if="btnVariant"
      :variant="btnVariant"
      :aria-label="buttonAriaLabel"
      @click="$emit('btnClicked')"
    />
  </header>
</template>
